import type { AkapellaBridge, BridgeHandle, DeviceInfo } from './types';

export type { AkapellaBridge, BridgeHandle, BridgeKind, DeviceInfo } from './types';

/** Dispatched on `window` by the MockBridge when `exitApp()` is called (dev overlay listens). */
export const MOCK_EXIT_EVENT = 'akp:mock-exit';

function hex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) out += bytes[i]!.toString(16).padStart(2, '0');
  return out;
}

/** RFC 4122 v4 UUID without `crypto.randomUUID` (Chromium 92+ only). */
export function uuidV4(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = hex(b);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/**
 * localStorage key the browser bridge keeps its device id under. An e2e harness INJECTS a
 * value here before the first load (`tests/e2e/src/flows.ts`) to pair a deterministic device;
 * the bridge simply finds it already stored and keeps it.
 */
export const DEVICE_ID_KEY = 'akp.deviceId';

/** `?fresh=1` — the escape hatch that mints a new TV without clearing site data. */
export const FRESH_PARAM = 'fresh';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota: keep the in-memory id for this run */
  }
}

/**
 * True when this load asked for a brand-new TV (`?fresh=1`). Only the browser bridge honours
 * it; the APK's device id comes from Android and can never be re-minted from the web layer.
 */
export function wantsFreshDevice(
  search = typeof window === 'undefined' ? '' : window.location.search,
): boolean {
  try {
    return new URLSearchParams(search).get(FRESH_PARAM) === '1';
  } catch {
    return false;
  }
}

export interface MockBridgeOptions {
  /** Mint (and persist) a new device id instead of reusing the stored one. */
  fresh?: boolean;
}

/**
 * Browser fallback: a UUID **persisted in localStorage** + no-op native calls (docs/04 §1).
 *
 * Reversal of the 2026-09-14 "fresh id per load" decision (Carlos, evening of the same day,
 * QA3 T2): turning the TV stick off and on with an active session has to resume it, and a
 * device id that changes on every load makes that impossible — the persisted Firebase user
 * would come back on a TV the API has never seen. Pairing is exercised repeatedly with the
 * `?fresh=1` escape hatch instead, which mints (and persists) a new id for this browser.
 */
export class MockBridge implements AkapellaBridge {
  private deviceId: string;
  /** True when the constructor minted a new id (first run ever, or `?fresh=1`). */
  readonly fresh: boolean;
  keepScreenOn = false;

  constructor(
    private readonly appVersion: string,
    { fresh = false }: MockBridgeOptions = {},
  ) {
    const stored = readStorage(DEVICE_ID_KEY);
    this.fresh = fresh || !stored;
    this.deviceId = this.fresh ? uuidV4() : stored!;
    if (this.deviceId !== stored) writeStorage(DEVICE_ID_KEY, this.deviceId);
  }

  getDeviceId(): string {
    return this.deviceId;
  }

  getDeviceInfo(): string {
    const info: DeviceInfo = {
      model: 'Browser',
      manufacturer: navigator.vendor || 'mock',
      androidVersion: '-',
      appVersion: this.appVersion,
    };
    return JSON.stringify(info);
  }

  setKeepScreenOn(on: boolean): void {
    this.keepScreenOn = on;
  }

  exitApp(): void {
    console.warn('[MockBridge] exitApp() called');
    window.dispatchEvent(new CustomEvent(MOCK_EXIT_EVENT));
  }

  log(level: string, msg: string): void {
    if (level === 'error') console.error(`[tv] ${msg}`);
    else console.warn(`[tv:${level}] ${msg}`);
  }
}

export function parseDeviceInfo(raw: string): DeviceInfo {
  try {
    const parsed = JSON.parse(raw) as Partial<DeviceInfo>;
    return {
      model: String(parsed.model ?? ''),
      manufacturer: String(parsed.manufacturer ?? ''),
      androidVersion: String(parsed.androidVersion ?? ''),
      appVersion: String(parsed.appVersion ?? ''),
    };
  } catch {
    return { model: '', manufacturer: '', androidVersion: '', appVersion: '' };
  }
}

let handle: BridgeHandle | null = null;

/** Detects `window.AkapellaBridge`; falls back to the MockBridge. Memoized. */
export function getBridge(appVersion = 'dev'): BridgeHandle {
  if (handle) return handle;
  const native = typeof window !== 'undefined' ? window.AkapellaBridge : undefined;
  const mock = native ? null : new MockBridge(appVersion, { fresh: wantsFreshDevice() });
  const bridge: AkapellaBridge = native ?? mock!;
  handle = {
    bridge,
    kind: native ? 'native' : 'mock',
    deviceId: bridge.getDeviceId(),
    info: parseDeviceInfo(bridge.getDeviceInfo()),
    // A device the APK reports is never "new"; only the browser can mint one.
    fresh: mock?.fresh === true && wantsFreshDevice(),
  };
  return handle;
}

/** Test helper. */
export function resetBridge(): void {
  handle = null;
}
